// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-09-10 17:22:11 +0200 (Thu, 10 Sep 2026) $
//  Author    : Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 165450 $
//  Purpose   : setup uix registry and import packages
//              distribute package artifacts in sub registries
// =============================================================================
// ---Third-party imports
import { each, iterItems, size, merge, dict, mapItems, get, keys, isArray } from 'ldash';
import { all } from 'redux-saga/effects';
// ---Framework imports
import log from 'uix/logging';
log.info('setup uix packages');
// this categorization can probably be disputed
// vls core
import * as config from './config';
import * as db from './db';
import * as domain from './domain';
import * as types from './types';
import * as states from './states';
import * as i18n from './i18n';
import * as auth from './auth';
import * as job from './job';
import * as upload from './upload';
// uix core
import * as network from './network';
import * as notify from './notify';
import * as viewer from './viewer';
import * as tree from './tree';
import * as templating from './templating';
import * as widgets from './widgets';
import * as logging from './logging';
import * as wiki from './wiki';
import * as frontend from './frontend';
import * as help from 'uix/help';
import * as persist from 'uix/persist';
// core modules (not in ./modules subdir)
let components = {
    network,
    logging,
    templating,
    debug,
    wiki,
    i18n,
    viewer,
    domain,
    types,
    tree,
    widgets,
    tests,
    notify,
    config,
    auth,
    states,
    job,
    upload,
    db,
    help,
    serverlog,
    frontend,
    persist
};
const nestedModuleNames = {};
let register = (name, module, path)=>{
    // XXX: path is currently not used
    // we can't get rid of the 'uix' prefix during importing
    nestedModuleNames[name] = path;
    components[name] = module;
};
// other modules
import * as tests from './tests';
// from ./modules subdirectory
import * as debug from './modules/debug';
register('debug', debug, 'uix/modules/debug');
import * as admin from './modules/admin';
register('admin', admin, 'uix/modules/admin');
import * as analytics from './modules/analytics';
register('analytics', analytics, 'uix/modules/analytics');
import * as mods from './modules/mods';
register('mods', mods, 'uix/modules/mods');
import * as preservation from './modules/preservation';
register('preservation', preservation, 'uix/modules/preservation');
import * as navigation from './modules/navigation';
register('navigation', navigation, 'uix/modules/navigation');
import * as resource from './modules/resource';
register('resource', resource, 'uix/modules/resource');
import * as stats from './modules/stats';
register('stats', stats, 'uix/modules/stats');
import * as fulltext from './modules/fulltext';
register('fulltext', fulltext, 'uix/modules/fulltext');
import * as transcription from './modules/transcription';
register('transcription', transcription, 'uix/modules/transcription');
import * as search from './modules/search';
register('search', search, 'uix/modules/search');
import * as imports from './modules/imports';
register('imports', imports, 'uix/modules/imports');
import * as serverlog from './modules/serverlog';
register('serverlog', serverlog, 'uix/modules/serverlog');
import * as database from './modules/database';
register('database', database, 'uix/modules/database');
import * as timeline from './modules/timeline';
register('timeline', timeline, 'uix/modules/timeline');
import * as qa_mods from './modules/qa';
register('qa', qa_mods, 'uix/modules/qa');
import * as gndo from './modules/gndo';
register('gndo', gndo, 'uix/modules/gndo');
import * as user from './modules/user';
register('user', user, 'uix/modules/user');
import * as presentation from './modules/presentation';
register('presentation', presentation, 'uix/modules/presentation');
import * as workshop from './modules/workshop';
register('workshop', workshop, 'uix/modules/workshop');
import * as pagebox from './modules/pagebox';
register('pagebox', pagebox, 'uix/modules/pagebox');
import * as vlm from './modules/vlm';
register('vlm', vlm, 'uix/modules/vlm');
import * as restriction from './modules/restriction';
register('restriction', restriction, 'uix/modules/restriction');
import * as scidesk from './modules/scidesk';
register('scidesk', scidesk, 'uix/modules/scidesk');
import * as glossary from './modules/glossary';
register('glossary', glossary, 'uix/modules/glossary');
// public toplevel component registry
// usage: dispatch(uix.wiki.actions.myAction())
const uix = components;
// register stuff in sub registries
// Handle Parts (react components) in an extra import path for hot-reloading
//~ import {Parts} from './parts'
// store names of components, that expose parts
const partModules = {};
// read static properties from part classes during registration
const partProperties = {};
// do the same with with wiki templates (also react comp rendering stuff)
// also import them at part importing time
const templateModules = {};
// register custom Job rendering components
// import them at part importing time
const jobModules = {};
// register custom dashboard panel components
const dashboardModules = {};
// register custom nodeMenu items
const nodeMenuModules = {};
// register custom table columns
const columnModules = {};
// collect all registered sagas here, simple dict
const Sagas = {};
const RunningSagas = {};
let sagaRunner = null;
const runSagas = (sagas)=>{
    if (!sagaRunner || size(sagas) === 0) {
        return;
    }
    let pending = [];
    keys(sagas).forEach((name)=>{
        if (!RunningSagas[name]) {
            RunningSagas[name] = true;
            pending.push(sagas[name]);
        }
    });
    if (size(pending) === 0) {
        return;
    }
    try {
        sagaRunner(function*() {
            yield all(pending);
        });
    } catch (error) {
        log.exception('saga registration failed', error);
    }
};
const registerSagas = (sagas)=>{
    if (size(sagas) > 0) {
        merge(Sagas, sagas);
        runSagas(sagas);
    }
};
const setSagaRunner = (runner)=>{
    sagaRunner = runner;
};
// collect selectors
import { Select } from './selectors';
// collect all registered views (templateconfig)
import { Views } from './views';
// all registered actions, simple dict
import { Actions } from './actions';
// XXX: should probably also collect
import * as Hooks from './hooks';
// list of reducers for store root sections
const Reducers = {
    config: [],
    meta: [],
    data: [],
    root: [],
    debug: []
};
iterItems(components, (name, comp)=>{
    // register parts (only by comp name due to hot loading)
    // see parts/index.js
    if (comp.parts) {
        partModules[name] = comp.parts;
    }
    // another hot loading special
    if (comp.templates) {
        templateModules[name] = comp.templates;
    }
    if (comp.jobs) {
        jobModules[name] = comp.jobs;
    }
    if (comp.dashpanels) {
        dashboardModules[name] = comp.dashpanels;
    }
    if (comp.nodemenu) {
        nodeMenuModules[name] = comp.nodemenu;
    }
    if (comp.columns) {
        columnModules[name] = comp.columns;
    }
    // the next ones are imported directly (non react comps)
    let log_msg = {};
    // register sagas
    if (size(comp.sagas) > 0) {
        registerSagas(comp.sagas);
        log_msg.sagas = [
            '%o sagas',
            size(comp.sagas)
        ];
    }
    // register reducers
    let registrations = {};
    each(keys(Reducers), (section)=>{
        let reducers = get(comp.reducers, section, []);
        if (size(reducers) > 0) {
            registrations[section] = size(reducers);
            Reducers[section] = [
                ...Reducers[section],
                ...reducers
            ];
        }
    });
    // only logging
    if (size(registrations) > 0) {
        let args = [];
        let msg = [];
        iterItems(registrations, (section, len)=>{
            args.push(len, section);
            msg.push('%o %s');
        });
        msg = msg.join(', ');
        log_msg.reducers = [
            `${msg} reducers`,
            args
        ];
    }
    // register actions
    if (size(comp.actions) > 0) {
        merge(Actions, comp.actions);
        log_msg.actions = [
            '%o actions',
            size(comp.actions)
        ];
    }
    // register selectors
    if (size(comp.selectors) > 0) {
        merge(Select, comp.selectors);
        log_msg.selectors = [
            '%o selectors',
            size(comp.selectors)
        ];
    }
    // register views
    if (size(comp.views) > 0) {
        merge(Views, comp.views);
        log_msg.views = [
            '%o views',
            size(comp.views)
        ];
    }
    // only logging from here
    let values = [];
    let msgs = [];
    keys(log_msg).filter((n)=>log_msg[n]).forEach((name)=>{
        let [msg, value] = log_msg[name];
        msgs.push(msg);
        values.push(...isArray(value) ? value : [
            value
        ]);
    });
    msgs = msgs.filter((e)=>e).join(' | ');
    if (msgs) {
        log.startup(`%o: ${msgs}`, name, ...values);
    }
});
export { Sagas, registerSagas, setSagaRunner, Reducers, Actions, Hooks, partModules, templateModules, dashboardModules, nodeMenuModules, jobModules, columnModules, nestedModuleNames, partProperties, Select as Sel, Views, uix,  }; // #if process.env.UIX_PROD_CHUNKED !== 'true'
 // require('./sagas/lazy_modules').registerNonChunkedSagas()
 // #endif
